// ===== Событийная мини-библиотека с namespaces =====
let guidCounter = 1;
const store = new WeakMap(); // baseType → Bucket
const fnIds = new WeakMap();
function getGuid(fn) {
    let id = fnIds.get(fn);
    if (!id) {
        id = guidCounter++;
        fnIds.set(fn, id);
    }
    return id;
}
function parseType(raw) {
    const parts = raw.split('.');
    const base = parts[0] ? parts[0].trim() : ''; // '' → форма '.ns'
    const namespaces = parts.slice(base ? 1 : 1) // все после первой точки
        .filter(Boolean)
        .map(s => s.toLowerCase().trim());
    namespaces.sort(); // порядок не важен → нормализуем
    const nsKey = namespaces.join('.') || '';
    return { base, namespaces, nsKey, nsSet: new Set(namespaces) };
}
function subset(sub, sup) {
    for (const s of sub)
        if (!sup.has(s))
            return false;
    return true;
}
function getBucket(target, baseType) {
    let map = store.get(target);
    if (!map) {
        map = new Map();
        store.set(target, map);
    }
    let bucket = map.get(baseType);
    if (!bucket) {
        const dispatcher = (ev) => {
            const curMap = store.get(target);
            const curBucket = curMap?.get(baseType);
            if (!curBucket)
                return;
            // копия, чтобы пережить remove во время вызова
            for (const h of [...curBucket.handlers]) {
                h.fn.call(target, ev);
            }
        };
        bucket = { handlers: [], dispatcher };
        map.set(baseType, bucket);
    }
    return bucket;
}
/** Подписка: поддерживает 'click', 'click.ns', 'keydown.form.v2' и т.п. */
function on(target, type, handler, options) {
    const { base, nsKey, nsSet } = parseType(type);
    if (!base)
        throw new Error(`on(): требуется базовый тип события, получено "${type}"`);
    const guid = getGuid(handler);
    const bucket = getBucket(target, base);
    // Первый хендлер этого типа? Вешаем общий dispatcher:
    if (bucket.handlers.length === 0) {
        bucket.options = options;
        target.addEventListener(base, bucket.dispatcher, options);
    }
    // Не считаем дублем, если namespaces отличаются
    const isDup = bucket.handlers.some(e => e.guid === guid && e.nsKey === nsKey);
    if (isDup)
        return;
    bucket.handlers.push({ fn: handler, guid, nsKey, nsSet });
}
/** Отписка:
 *  - off(el) — снять всё
 *  - off(el, '.ns') — снять все типы для этого(их) неймспейса(ов)
 *  - off(el, 'click') — снять все click
 *  - off(el, 'click.ns') — снять click только с этим неймспейсом(ами)
 *  - off(el, 'click.ns', fn) — снять только конкретную связку
 *  - off(el, undefined, fn) — снять все связки этой функции (всех типов/неймспейсов)
 */
function off(target, type, handler) {
    const map = store.get(target);
    if (!map)
        return;
    // Хелпер: снять бакет, если он пустой
    const cleanupBucket = (base, bucket) => {
        if (bucket.handlers.length === 0) {
            target.removeEventListener(base, bucket.dispatcher, bucket.options);
            map.delete(base);
        }
    };
    // Снять всё
    if (!type && !handler) {
        for (const [base, bucket] of map) {
            target.removeEventListener(base, bucket.dispatcher, bucket.options);
        }
        store.delete(target);
        return;
    }
    // Если указан только handler — удаляем все его связки (любые типы/неймспейсы)
    if (!type && handler) {
        const gid = fnIds.get(handler);
        if (!gid)
            return;
        for (const [base, bucket] of map) {
            bucket.handlers = bucket.handlers.filter(e => e.guid !== gid);
            cleanupBucket(base, bucket);
        }
        if (map.size === 0)
            store.delete(target);
        return;
    }
    // Разобрать type → базовый тип и фильтр по неймспейсам
    const { base, nsSet: wantNs } = parseType(type);
    // off(el, '.ns') — без базового типа: пройтись по всем типам
    const bases = base ? [base] : Array.from(map.keys());
    const gid = handler ? fnIds.get(handler) : undefined;
    for (const b of bases) {
        const bucket = map.get(b);
        if (!bucket)
            continue;
        bucket.handlers = bucket.handlers.filter(e => {
            // фильтр по handler (если задан)
            if (gid && e.guid !== gid)
                return true;
            // фильтр по namespaces (если задан): снимаем, если wantNs ⊆ e.nsSet
            if (wantNs.size > 0 && !subset(wantNs, e.nsSet))
                return true;
            // Если namespaces не заданы (off(el,'click')), снимаем все этого типа (с любыми ns)
            if (wantNs.size === 0 && base) {
                return false; // удалить
            }
            // Иначе все условия совпали → удалить
            return false;
        });
        cleanupBucket(b, bucket);
    }
    if (map.size === 0)
        store.delete(target);
}
/** Снять всё с target (эквивалент off(target)) */
function offAll(target) {
    off(target);
}

export { off, offAll, on };
